import React from "react";
import styled from "styled-components";
import { pdf } from "@react-pdf/renderer";
import { SearchResultsPDFDocument } from "../pdf/SearchResultsPDFDocument";
import { Timeline } from "../Timeline";
import { SearchResult } from "../../services/search";
import { formatTimelineItems } from "../../utils/searchUtils";
import { EmptyState } from "../records/EmptyState";

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1.2rem 0 0.8rem 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const DownloadButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  padding: 6px 12px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
`;

const ResultsCount = styled.span`
  font-weight: 500;
`;

interface SearchResultsProps {
  results: SearchResult[];
  selectedDevice: string | null;
  parseMetadata: (raw: any) => any;
  onItemClick: (item: any) => void;
  searchQuery?: string;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  selectedDevice,
  parseMetadata,
  onItemClick,
  searchQuery,
}) => {
  if (results.length === 0) {
    return (
      <EmptyState
        title="No Results Found"
        message={
          searchQuery ? `No records match "${searchQuery}".` : "There are no device records available."
        }
      />
    );
  }

  const handleDownloadPdf = async () => {
    try {
      const blob = await pdf(
        <SearchResultsPDFDocument results={results} parseMetadata={parseMetadata} query={searchQuery} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `search-results-${Date.now()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF generation failed", e);
    }
  };

  return (
    <>
      <ResultsHeader>
        <ResultsCount>
          {results.length} record{results.length !== 1 ? "s" : ""} found
          {searchQuery && ` for "${searchQuery}"`}
        </ResultsCount>
        {results.length > 0 && <DownloadButton onClick={handleDownloadPdf}>Download PDF</DownloadButton>}
      </ResultsHeader>

      <Timeline items={formatTimelineItems(results, parseMetadata)} onItemClick={onItemClick} />
    </>
  );
};
